import React, { useEffect, useRef, useState } from 'react';
import { Bubble, Button } from 'tea-component';
import { ReactSortable } from 'react-sortablejs';
import SvgIcon from '@home/components/SvgIcon';
import type { IntlShape } from 'umi';

import classNames from 'classnames';
import { useIntl } from 'umi';
import { useOutsideClick } from 'tea-component/es/_util/use-outside-click';

import { ChangeTabLayoutsType } from '@dbl/core/dashboard';
import type {
  ILayoutItemFilter,
  TChangeTabLayoutsHandler,
} from '@dbl/core/dashboard';
import {
  errorClassNameMap
} from "@/pages/datalab/components/DashboardLayout/components/TopFilters/GeneralFilterGroup/index";

export interface IFilterErrorInfo {
  code?: number;
  message?: string;
  [key: string]: unknown;
}

export interface IFilterEditRenderProps {
  itemKey: string;
  filterInfo: ILayoutItemFilter;
  deleteVisible: boolean;
  errorInfo?: IFilterErrorInfo;
  setErrorInfo?: (info: IFilterErrorInfo) => void;
}

interface IFilterEditItemProps {
  filter: ILayoutItemFilter;
  changeLeftLayoutSelectItem?: (filter: ILayoutItemFilter, isOpen?: boolean) => void;
  formatMessage: IntlShape['formatMessage'];
  changeTabLayoutsReduce?: TChangeTabLayoutsHandler;
  children: (props: IFilterEditRenderProps) => React.ReactNode;
}

interface IFilterEditProps {
  filters: ILayoutItemFilter[];
  changeTabLayoutsReduce?: TChangeTabLayoutsHandler;
  changeLeftLayoutSelectItem?: (filter: ILayoutItemFilter, isOpen?: boolean) => void;
  children: (props: IFilterEditRenderProps) => React.ReactNode;
}

const FilterEditItem: React.FC<IFilterEditItemProps> = ({
  filter,
  changeLeftLayoutSelectItem,
  formatMessage,
  changeTabLayoutsReduce,
  children,
}) => {
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [errorInfo, setErrorInfo] = useState<IFilterErrorInfo>({});
  const deleteRef = useRef<HTMLDivElement | null>(null);
  const deleteBubbleRef = useRef<HTMLDivElement | null>(null);
  const { listen } = useOutsideClick([deleteRef, deleteBubbleRef]);
  useEffect(() => {
    listen(() => {
      setDeleteVisible(false);
    });
  }, [listen]);

  // fix：date和number不请求接口,重置error状态
  const errorClassName = !(['date', 'number']?.includes(filter?.filterType || '')) && errorClassNameMap[errorInfo?.code];

  return (<Bubble
    key={filter.i}
    ref={deleteBubbleRef}
    // trigger={'click'}
    escapeWithReference
    placement={'bottom'}
    // placementOffset={[filter.filterType === 'date' ? 137 : 81, 10]}
    visible={deleteVisible}
    closeOnScroll
    content={(<div className={'dbl-filter-drag-delete-bubble'}>
      <div className={'dbl-filter-drag-delete-bubble-header'}>
        <SvgIcon type={'alert-yellow'}/>
        {formatMessage({id: 'dbl_create_dashboard_delete_filter_title'})}
      </div>
      <div className={'dbl-filter-drag-delete-bubble-body'}>
        {formatMessage({id: 'dbl_create_dashboard_delete_filter_content'})}
      </div>
      <div className={'dbl-filter-drag-delete-bubble-footer'}>
        <Button type="primary" onClick={() => {
          // e.stopPropagation();
          // TODO filterValueMap?????
          changeTabLayoutsReduce?.(ChangeTabLayoutsType.DELETE_FILTER, {filterId: filter?.i, isRefresh: !!filter.value});
        }}>{formatMessage({id: 'dbl_common_confirm'})}</Button>
        <Button onClick={() => {
          // e.stopPropagation();
          setDeleteVisible(false);
        
        }}>{formatMessage({id: 'dbl_common_cancel'})}</Button>
      </div>
    </div>)}
    overlayClassName={'dbl-dashboard-filter-delete-bubble'}
    referenceElement={deleteRef.current}
    // transitionTimeout 设置 exit 为0, 是为了防止 在点击取消按钮时, 浮窗内容闪现在页面左上角后再消失
    transitionTimeout={{exit: 0}}
  >
    <div
      className={classNames(
        'dbl-filter-drag-wrap',
        filter.filterType,
        errorClassName,
        {
          'dbl-filter-drag-hover': deleteVisible,
        },
      )}
    >
      {
        children({ itemKey: filter.i, filterInfo: filter, deleteVisible, errorInfo, setErrorInfo })
      }
      <div
        className="dbl-filter-drag-edit dbl-filter-drag-btn"
        onClick={(e) => {
          e.stopPropagation();
          changeLeftLayoutSelectItem?.(filter, true);
        }}
      >
        <SvgIcon type="pencil-m" className="dbl-filter-drag-icon"/>
      </div>
    
      <div
        ref={deleteRef}
        className="dbl-filter-drag-delete dbl-filter-drag-btn"
        onClick={(e) => {
          e.stopPropagation();
          // setVisibleScope(false);
          if (filter?.value) { // 判断不是空筛选器, 弹出弹窗提示
            setDeleteVisible(true);
          } else {
            // TODO isRefresh
            changeTabLayoutsReduce?.(ChangeTabLayoutsType.DELETE_FILTER, {filterId: filter?.i, isRefresh: !!filter.value});
          }
        }}
      >
        <SvgIcon type="clonse" className="dbl-filter-drag-icon"/>
      </div>
  
    </div>
  </Bubble>);
};

const FilterEdit: React.FC<IFilterEditProps> = ({
  filters,
  changeTabLayoutsReduce,
  changeLeftLayoutSelectItem,
  children,
}) => {
  const {formatMessage} = useIntl();
  
  return (
    <ReactSortable
      className={'dbl-dashboard-filter-wrap-sortable'}
      sort={true}
      group={{
        name: 'dashboardFilterWrap',
        pull: true,
        put: true,
      }}
      animation={200}
      handle={'.dbl-filter-drag-wrap'}
      ghostClass="dbl-filter-drag-ghost"
      dragClass="dbl-filter-drag-drag"
      // delay={100}
      list={filters}
      // forceFallback={true}
      setList={(list) => {
        if (JSON.stringify(filters) !== JSON.stringify(list)) {
          if (list.length > filters.length) {
            // 如果拖拽到这个区域的元素是 tab 或者 text，那么将不增加筛选器
            const notFilterItem = list.find(item => item?.type !== 'filter');
            !notFilterItem && changeTabLayoutsReduce?.(ChangeTabLayoutsType.ADD_FILTER);
          } else {
            changeTabLayoutsReduce?.(ChangeTabLayoutsType.CHANGE_FILTER_ORDER, {filters: list});
          }
        }
      }}
    >
      {filters.map(item => (
        <FilterEditItem
          key={item.i}
          filter={item}
          formatMessage={formatMessage}
          changeLeftLayoutSelectItem={changeLeftLayoutSelectItem}
          changeTabLayoutsReduce={changeTabLayoutsReduce}
        >
          {children}
        </FilterEditItem>
      ))}
      <div
        className={'dbl-dashboard-filter-wrap-add-filter'}
        onClick={() => {
          // 添加筛选器
          changeTabLayoutsReduce?.(ChangeTabLayoutsType.ADD_FILTER);
          // 添加筛选器的同时, 右侧设置要显示筛选器配置页面
        }}
      >
        <SvgIcon type={'cross'}/>
        {formatMessage({id: 'dbl_create_dashboard_add_filter'})}
      </div>
    </ReactSortable>
  );
};

export default FilterEdit;
